import React, { useState } from 'react';
import { Plus, FileText, PanelTop, Home, ChevronRight, ChevronDown, Megaphone } from 'lucide-react';
import { Button } from '@components/ui/button';
import useEditorStore, { LayoutEntity, PostEntity } from './editorStore';

/**
 * Left rail of the canvas editor — every surface an admin can author:
 * - Home Page Posts (the public homepage feed)
 * - Pages (nav-header layouts; the name is the public URL; inactive pages
 *   are hidden from the nav — that's the schema's `active` flag)
 * - Home (fallback page): shown publicly only while no posts are published
 * - Division callouts: per-division blurbs above the public schedules
 *
 * Season/tournament-TIER layouts are deliberately absent: no public surface
 * ever rendered them, so editing them was editing the void (they'll be
 * cleaned with migration 21's audit).
 */

export interface DivisionItem {
  _id: string;
  name: string;
  layout: LayoutEntity | null;
}

export interface SeasonGroup {
  _id: string;
  name: string;
  divisions: DivisionItem[];
}

interface Props {
  posts: PostEntity[];
  pages: LayoutEntity[];
  fallbackHome: LayoutEntity | null;
  seasonGroups: SeasonGroup[];
  tournamentGroups: SeasonGroup[];
  creating: boolean;
  onSelectPost: (post: PostEntity) => void;
  onNewPost: () => void;
  onSelectLayout: (layout: LayoutEntity) => void;
  onNewPage: () => void;
  onSelectFallbackHome: () => void;
  onSelectDivision: (division: DivisionItem) => void;
}

export default function CanvasSidebar({
  posts,
  pages,
  fallbackHome,
  seasonGroups,
  tournamentGroups,
  creating,
  onSelectPost,
  onNewPost,
  onSelectLayout,
  onNewPage,
  onSelectFallbackHome,
  onSelectDivision,
}: Props) {
  const selected = useEditorStore((s) => s.selected);
  const selectedPostId = selected?.kind === 'post' ? selected.id : null;
  const selectedLayoutId = selected?.kind === 'layout' ? selected.id : null;

  return (
    <aside className="canvas-sidebar">
      <div className="canvas-sidebar__section">
        <div className="canvas-sidebar__heading">
          <span>Home Page Posts</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onNewPost}
            disabled={creating}
            aria-label="New post"
            title="New post"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {posts.length === 0 && (
          <p className="canvas-sidebar__empty">No posts yet. Click + to write your first post.</p>
        )}
        <ul>
          {posts.map((post) => (
            <li key={post._id}>
              <button
                type="button"
                className={`canvas-sidebar__item ${selectedPostId === post._id ? 'canvas-sidebar__item--active' : ''}`}
                onClick={() => onSelectPost(post)}
              >
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{post.title || 'Untitled post'}</span>
                {post.pinned === true && <span className="canvas-sidebar__draft canvas-sidebar__pinned">Pinned</span>}
                {post.status !== 'published' && <span className="canvas-sidebar__draft">Draft</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="canvas-sidebar__section">
        <div className="canvas-sidebar__heading">
          <span>Pages</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onNewPage}
            disabled={creating}
            aria-label="New page"
            title="New page"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {pages.length === 0 && (
          <p className="canvas-sidebar__empty">No pages yet. Click + to add one to your site&apos;s navigation.</p>
        )}
        <ul>
          {pages.map((page) => (
            <li key={page._id}>
              <button
                type="button"
                className={`canvas-sidebar__item ${selectedLayoutId === page._id ? 'canvas-sidebar__item--active' : ''}`}
                onClick={() => onSelectLayout(page)}
              >
                <PanelTop className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{page.name || 'Untitled page'}</span>
                {page.active === false && <span className="canvas-sidebar__draft">Hidden</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="canvas-sidebar__section">
        <div className="canvas-sidebar__heading">
          <span>Fallback</span>
        </div>
        <button
          type="button"
          className={`canvas-sidebar__item ${
            fallbackHome && selectedLayoutId === fallbackHome._id ? 'canvas-sidebar__item--active' : ''
          }`}
          onClick={onSelectFallbackHome}
        >
          <Home className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">Home (fallback page)</span>
        </button>
        <p className="canvas-sidebar__note">Shown only while no posts are published.</p>
      </div>

      {seasonGroups.length > 0 && (
        <GroupSection
          title="Division Callouts"
          groups={seasonGroups}
          selectedLayoutId={selectedLayoutId}
          onSelectDivision={onSelectDivision}
        />
      )}
      {tournamentGroups.length > 0 && (
        <GroupSection
          title="Tournament Callouts"
          groups={tournamentGroups}
          selectedLayoutId={selectedLayoutId}
          onSelectDivision={onSelectDivision}
        />
      )}
    </aside>
  );
}

function GroupSection({
  title,
  groups,
  selectedLayoutId,
  onSelectDivision,
}: {
  title: string;
  groups: SeasonGroup[];
  selectedLayoutId: string | null;
  onSelectDivision: (division: DivisionItem) => void;
}) {
  return (
    <div className="canvas-sidebar__section">
      <div className="canvas-sidebar__heading">
        <span>{title}</span>
      </div>
      {groups.map((group) => (
        <SeasonBranch
          key={group._id}
          group={group}
          selectedLayoutId={selectedLayoutId}
          onSelectDivision={onSelectDivision}
        />
      ))}
    </div>
  );
}

function SeasonBranch({
  group,
  selectedLayoutId,
  onSelectDivision,
}: {
  group: SeasonGroup;
  selectedLayoutId: string | null;
  onSelectDivision: (division: DivisionItem) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" className="canvas-sidebar__item" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        <span className="truncate">{group.name}</span>
      </button>
      {open && (
        <ul className="canvas-sidebar__children">
          {group.divisions.length === 0 && <p className="canvas-sidebar__empty">No divisions.</p>}
          {group.divisions.map((division) => (
            <li key={division._id}>
              <button
                type="button"
                className={`canvas-sidebar__item ${
                  division.layout && selectedLayoutId === division.layout._id
                    ? 'canvas-sidebar__item--active'
                    : ''
                }`}
                onClick={() => onSelectDivision(division)}
              >
                <Megaphone className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{division.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
